import React from "react"
import { v4 as uuidv4 } from "uuid"
import { SearchspringSearchResult } from "@app/types"

export const storeFrontAccessToken = "602f8b5dec7379dba72615f89a17cc6a"

export function generateId() {
  return uuidv4()
}

export const parseIfString = (input) => {
  if (typeof input === "string") {
    try {
      return JSON.parse(input.replace(/&quot;/g, '"'))
    } catch {
      return []
    }
  }
  return input || []
}

export const isMobile = window.innerWidth < 768

// Resolves a raw variant size value to its display label. Legacy numeric sizes
// map to alpha sizing via window.theme.sizes (serialised from the sizing
// metaobject in theme.liquid); unmapped values pass through unchanged.
// Display-only: never use the output for variant matching or filter values.
export const sizeLabel = (value?: string | null): string => {
  if (!value) return ""
  if (typeof window === "undefined") return value
  return window.theme?.sizes?.[value] || value
}

// Gets the current country currency from Shopify data or defaults to AUD
export const currentCountryCurrency =
  typeof window !== "undefined" &&
  window.SHOPIFY_DATA &&
  window.SHOPIFY_DATA.currency
    ? window.SHOPIFY_DATA.currency
    : { countryCode: "AU", currencyCode: "AUD", currencySymbol: "$" }

export function stripHTML(html: string): string {
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = html
  return tempDiv.textContent || ""
}

export function decodeHTMLEntities(text: string): string {
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = text
  return tempDiv.textContent || text
}

// Product Utils
export function useProductBadge(
  data: SearchspringSearchResult,
  available: boolean
) {
  // Coming Soon is template-driven and outranks every other badge, mirroring the Liquid
  // PDP badge (product-card-badge.liquid). Requires the `template_suffix` field to be
  // enabled for Display in the Searchspring field config so it is returned on each result.
  if (data?.template_suffix?.includes("coming-soon")) {
    return <span className="badge coming-soon">Coming Soon</span>
  }

  // Tag-driven badges are optional; the "Almost Gone" badge (#1802) is derived
  // from stock alone, so it must not be gated on tag presence.
  const tagBadges = (data?.tags ?? [])
    .filter((tag: string) => tag?.toLowerCase()?.includes("badge:"))
    .map((tag: string) => tag?.toLowerCase()?.split("badge:")?.[1].trim())

  const preorderBadge = tagBadges.includes("pre-order")
  const customBadge = tagBadges.find(
    (b: string) =>
      b !== "pre-order" &&
      b !== "out of stock" &&
      b !== "sale" &&
      b !== "almost gone"
  )
  const saleBadge = tagBadges.includes("sale")

  // Automatic "Almost Gone" (#1802, rule widened 2026-08-14): true when three
  // or more sizes are sold out while at least one size remains in stock. The
  // "-" one-size placeholder is ignored, so one-size products never qualify and
  // fully sold-out products are excluded. Colourways are separate products, so
  // evaluating per product scopes this to the individual colour. Mirrors
  // snippets/product-almost-gone.liquid.
  const badgeVariants = parseIfString(data?.variants) as Array<{
    option2?: string
    inventory_quantity?: number
    available?: boolean
  }>
  const sizeInStock: Record<string, boolean> = {}
  badgeVariants.forEach((variant) => {
    const size = variant?.option2?.trim()
    if (!size || size === "-") return
    const inStock =
      typeof variant?.inventory_quantity === "number"
        ? variant.inventory_quantity > 0
        : !!variant?.available
    sizeInStock[size] = sizeInStock[size] || inStock
  })
  const sizeStates = Object.values(sizeInStock)
  const soldOutSizes = sizeStates.filter((inStock) => !inStock).length
  const almostGone = soldOutSizes >= 3 && sizeStates.length - soldOutSizes >= 1

  const handleize = (str: string) => {
    return str
      .toLowerCase() // Convert to lowercase
      .replace(/[^\w\s-]/g, "") // Remove non-alphanumeric characters (except hyphens and spaces)
      .replace(/[\s_-]+/g, "-") // Replace spaces, underscores, and multiple hyphens with a single hyphen
      .replace(/^-+|-+$/g, "") // Trim leading/trailing hyphens
  }

  if (preorderBadge) {
    return <span className="badge pre-order">Pre Order</span>
  } else if (!available) {
    return <span className="badge out-of-stock">Out of Stock</span>
  } else if (almostGone) {
    return <span className="badge almost-gone">Almost Gone</span>
  } else if (customBadge) {
    const handle = handleize(customBadge.toLowerCase())

    return <div className={`badge ${handle}`}>{customBadge}</div>
  } else if (
    available &&
    (Number(data?.msrp) > Number(data?.price) || saleBadge)
  ) {
    return <span className="badge on-sale">Sale</span>
  }

  return ""
}

// sanitizes strings to replace all spaces, slashes and underscores with a dash
export function normalizeHandleDash(input: string) {
  // check if handle starts with underscore and then removes it
  // This is connected to a colour swatch graphql alias issue
  if (input.startsWith("_")) {
    input = input.slice(1)
  }

  return input.toLowerCase().replace(/[\s/_]+/g, "-")
}

export async function fetchStorefront(query: string) {
  const endpoint = `https://${Shopify.shop}/api/2025-04/graphql.json`
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storeFrontAccessToken,
    },
    body: JSON.stringify({ query }),
  })
  const { data, errors } = await res.json()
  if (errors) {
    console.error("Storefront GraphQL errors:", errors)
    throw new Error(errors.map((e: string) => e.message).join("\n"))
  }
  return data
}
